/**
 * Media thumbnail + mime helpers — REUSE of the Quick Capture app's pattern.
 *
 * Mirrors `Quick_Capture/capture-app/js/utils.js`
 * (`generateImageThumbnail` / `generateVideoThumbnail` / `getFileExtension`)
 * and `capture.js`'s `_defaultMime`, ported to TypeScript so the L1A intake
 * Photos & videos section produces the SAME thumbnails the capture app does:
 *   - photos → a downscaled JPEG snapshot (canvas);
 *   - videos → a mid-clip frame snapshot (seek + canvas), with a graceful
 *     null fallback when the browser can't decode the frame.
 *
 * Pure DOM (canvas / Image / video elements) — no Firebase, no React.
 */

const DEFAULT_MAX = 200;

/** mime → file extension (capture-app utils.js getFileExtension). */
export function getFileExtension(mimeType: string | undefined): string {
  const map: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/heic': 'heic',
    'audio/webm': 'webm',
    'audio/mp4': 'm4a',
    'audio/ogg': 'ogg',
    'audio/mpeg': 'mp3',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/quicktime': 'mov',
  };
  return map[mimeType || ''] || 'bin';
}

/** Default mime when the File carries none (capture-app capture.js _defaultMime). */
export function defaultMime(kind: 'image' | 'video'): string {
  return kind === 'video' ? 'video/mp4' : 'image/jpeg';
}

/** image/* vs video/* from a File (mirrors the capture app's type routing). */
export function mediaKindOf(file: File): 'image' | 'video' {
  return (file.type || '').startsWith('video') ? 'video' : 'image';
}

/**
 * Downscaled JPEG thumbnail for an image File (capture-app generateImageThumbnail).
 * Resolves to an object-URL for the thumbnail blob, or null on failure.
 */
export function generateImageThumbnail(file: File, maxSize = DEFAULT_MAX): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let w = img.width;
      let h = img.height;
      if (w > h) {
        h = Math.round((h * maxSize) / w);
        w = maxSize;
      } else {
        w = Math.round((w * maxSize) / h);
        h = maxSize;
      }
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          resolve(blob ? URL.createObjectURL(blob) : null);
        },
        'image/jpeg',
        0.7,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/**
 * Mid-clip frame thumbnail for a video File (capture-app generateVideoThumbnail).
 * Resolves to an object-URL for the frame blob, or null when the frame can't be
 * decoded (Safari/codec edge cases) — caller falls back to a 🎬 placeholder.
 */
export function generateVideoThumbnail(file: File, maxSize = DEFAULT_MAX): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');

    let resolved = false;
    const done = (out: string | null) => {
      if (resolved) return;
      resolved = true;
      URL.revokeObjectURL(url);
      resolve(out);
    };
    const timeout = setTimeout(() => done(null), 5000);

    const drawFrame = () => {
      if (resolved) return;
      clearTimeout(timeout);
      try {
        const canvas = document.createElement('canvas');
        let w = video.videoWidth || maxSize;
        let h = video.videoHeight || maxSize;
        if (w > h) {
          h = Math.round((h * maxSize) / w);
          w = maxSize;
        } else {
          w = Math.round((w * maxSize) / h);
          h = maxSize;
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return done(null);
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob((blob) => done(blob ? URL.createObjectURL(blob) : null), 'image/jpeg', 0.7);
      } catch {
        done(null);
      }
    };

    video.onseeked = drawFrame;
    video.onloadeddata = () => {
      if (video.duration > 0) video.currentTime = Math.min(1, video.duration / 2);
      else drawFrame();
    };
    video.oncanplay = () => {
      if (!video.videoWidth) {
        // Safari fallback — nudge a play()/seek to populate videoWidth.
        void video.play().then(() => {
          video.pause();
          drawFrame();
        }).catch(() => drawFrame());
      }
    };
    video.onerror = () => done(null);
    video.src = url;
  });
}

/** Best-effort thumbnail for any media File — picks image/video path by type. */
export async function generateThumbnail(file: File): Promise<string | null> {
  return mediaKindOf(file) === 'video'
    ? generateVideoThumbnail(file)
    : generateImageThumbnail(file);
}
